import React, { useMemo, useState } from 'react';
import { PiRulerLight } from 'react-icons/pi';
import { LuSettings2 } from 'react-icons/lu';
import { BsArrowsAngleExpand } from 'react-icons/bs';
import { RiBallPenLine } from 'react-icons/ri';

import { Button, Menu } from 'antd';
import './CustomMapMenu.scss';
import { useLegendUI } from '../../../context/MapLegendContext';
import CreateFeatureModal from '../../../views/MapPortal/Tools/CreateFeatureModal';
import QueryModal from '../../../views/MapPortal/Tools/QueryToolModal';
import ProximityModal from '../../../views/MapPortal/Tools/ProximityToolModal';
import { DrawerItem } from '../../../views/MapPortal/Legend/LegendDrawer';
import MapMenu from '../../../assets/icons/map-menu.svg';
import { FileTypes } from '../../../enums/fileTypes';
import { useAuth } from '../../../context/AuthContext';
import { LayerAction } from '../../../enums/mapPortalActions';
import { layerActionAccess } from '../../../views/MapPortal/constants';
import ConditionalRender from '../ConditionalRender';

interface CustomMapMenuProps {
  drawingMode?: string;
  setDrawingMode?: (value: any) => void;
  drawerItems?: DrawerItem[];
  setDrawerItems?: React.Dispatch<React.SetStateAction<any[]>>;
  setCurrentTool?: (value: string) => void;
  settings?: {
    queryTool: { enabled: boolean; name: string; color: string };
    proximityTool: { enabled: boolean; name: string; color: string };
  };
  mapId?: string;
  viewerRef?: any;
  setIsLayerLoading?: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
}

const CustomMapMenu: React.FC<CustomMapMenuProps> = ({
  drawerItems,
  setDrawerItems,
  setCurrentTool,
  settings,
  mapId,
  viewerRef,
  setIsLayerLoading,
  onLayerUpdateComplete,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  // US-104110/108781: Create Feature is Admin/Editor only — hidden from Viewers.
  const { canAccess } = useAuth();
  const canEdit = canAccess(layerActionAccess(LayerAction.OPEN_CREATE_FEATURE));
  const filteredLayers = useMemo(
    () =>
      (drawerItems ?? []).filter((item) => item.layerType !== FileTypes.RASTER && item.layerType !== FileTypes.WMS && item.publishStatus === 'ready'),
    [drawerItems],
  );
  const {
    drawFeatureModalVisible,
    setDrawFeatureModalVisible,
    queryToolModalVisible,
    setQueryToolModalVisible,
    queryResultModalVisible,
    setQueryResultModalVisible,
    setProximityToolModalVisible,
    proximityToolModalVisible,
  } = useLegendUI();

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const closeAllTools = () => {
    setCurrentTool?.('');
    setDrawFeatureModalVisible(false);
    setQueryToolModalVisible(false);
    setProximityToolModalVisible(false);
    setQueryResultModalVisible(false);
  };

  const handleItemClick = (action: () => void) => {
    closeAllTools();
    action();
    setCollapsed(true); // Close menu after a tool is selected
  };

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
        }}
      >
        <Button
          onClick={toggleCollapsed}
          type="primary"
          className="map-menu-trigger"
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 1000,
            backgroundColor: 'transparent',
          }}
        >
          <span className="map-Menu">
            <MapMenu />
          </span>
        </Button>
        {!collapsed && (
          <Menu className="map-menu-panel" mode="inline" inlineCollapsed={collapsed} selectable={false}>
            <Menu.Item key="1" icon={<PiRulerLight />} onClick={() => handleItemClick(() => setCurrentTool?.('measure'))}>
              MEASURE
            </Menu.Item>
            {settings?.queryTool.enabled && (
              <Menu.Item key="2" icon={<LuSettings2 />} onClick={() => handleItemClick(() => setQueryToolModalVisible(true))}>
                QUERY
              </Menu.Item>
            )}
            {settings?.proximityTool.enabled && (
              <Menu.Item key="3" icon={<BsArrowsAngleExpand />} onClick={() => handleItemClick(() => setProximityToolModalVisible(true))}>
                PROXIMITY
              </Menu.Item>
            )}
            {canEdit && (
              <Menu.Item
                key="4"
                icon={<RiBallPenLine />}
                onClick={() =>
                  handleItemClick(() => {
                    setDrawFeatureModalVisible(true);
                    setCurrentTool?.('create feature');
                  })
                }
              >
                CREATE FEATURE
              </Menu.Item>
            )}
          </Menu>
        )}
      </div>

      <ConditionalRender visible={canEdit && drawFeatureModalVisible}>
        <CreateFeatureModal
          visible={drawFeatureModalVisible}
          setVisible={setDrawFeatureModalVisible}
          mapId={mapId}
          viewerRef={viewerRef}
          setCurrentTool={setCurrentTool}
          onLayerUpdateComplete={onLayerUpdateComplete}
        />
      </ConditionalRender>

      {drawerItems?.length ? (
        <ProximityModal
          proximityModalToolVisible={proximityToolModalVisible}
          setProximityModalToolVisible={setProximityToolModalVisible}
          drawerItems={filteredLayers}
          setDrawerItems={setDrawerItems!}
          settings={settings}
          mapId={mapId}
          viewerRef={viewerRef}
          setCurrentTool={setCurrentTool}
          setIsLayerLoading={setIsLayerLoading}
          onLayerUpdateComplete={onLayerUpdateComplete}
        />
      ) : null}

      {drawerItems?.length && (
        <QueryModal
          queryModalToolVisible={queryToolModalVisible}
          setQueryModalToolVisible={setQueryToolModalVisible}
          drawerItems={filteredLayers}
          setDrawerItems={setDrawerItems!}
          queryResultModalVisible={queryResultModalVisible}
          setQueryResultModalVisible={setQueryResultModalVisible}
          settings={settings}
          mapId={mapId}
          viewerRef={viewerRef}
          setCurrentTool={setCurrentTool}
          setIsLayerLoading={setIsLayerLoading}
          onLayerUpdateComplete={onLayerUpdateComplete}
        />
      )}
    </>
  );
};

export default CustomMapMenu;
